import React, { useEffect, useCallback, useState } from 'react';
import BeneficiaryMultiValues from '../BeneficiaryValues/BeneficiaryMultiValues';
import { MainCategoryProps } from '../MainCategory';
import PercentageFieldWithLabel from '../PercentageFieldWithLabel';
import {
  Beneficiaries,
  Beneficiary,
  BeneficiaryAge,
  BeneficiaryPercentage
} from '../utils/beneficiaryInterfaces';
import {
  getBeneficiaryValue,
  setBeneficiaryValue
} from '../utils/helperFunctions';

const isDisabled = (
  name: Beneficiary<BeneficiaryAge>['name'],
  allAdults: BeneficiaryPercentage,
  nineteen: BeneficiaryPercentage,
  seniors: BeneficiaryPercentage
): boolean => {
  switch (name) {
    case 'Adults':
      return !!(nineteen || seniors);
    case 'Nineteen and older':
    case 'Seniors':
      return !!allAdults;
    default:
      return true;
  }
};

export interface BeneficiaryAgeProps {
  beneficiary: Beneficiaries['age'];
  setBeneficiaries: MainCategoryProps['setBeneficiaries'];
  beneficiaryLabel?: string;
}

const BeneficiaryAdultValues: React.FC<BeneficiaryAgeProps> = ({
  beneficiary,
  setBeneficiaries
}) => {
  const [allAdults, setAllAdults] = useState<BeneficiaryPercentage>(
    getBeneficiaryValue(beneficiary?.categories, 'Adults')
  );
  const [nineteen, setNineteen] = useState<BeneficiaryPercentage>(
    getBeneficiaryValue(beneficiary?.categories, 'Nineteen and older')
  );
  const [seniors, setSeniors] = useState<BeneficiaryPercentage>(
    getBeneficiaryValue(beneficiary?.categories, 'Seniors')
  );
  const [errorMessage] = useState<string | undefined>();

  const saveBeneficiaryAdults = useCallback(
    (category: BeneficiaryAge, percentage: BeneficiaryPercentage) => {
      setBeneficiaries(s => ({
        ...s,
        age: {
          categories: setBeneficiaryValue(
            s.age?.categories,
            category,
            percentage
          )
        }
      }));
    },
    [setBeneficiaries]
  );

  const saveAllAdults = useCallback(
    (percentage: BeneficiaryPercentage) => {
      saveBeneficiaryAdults('Adults', percentage);
      setAllAdults(percentage);
    },
    [beneficiary?.categories, setBeneficiaries, setAllAdults]
  );
  const saveNineteen = useCallback(
    (percentage: BeneficiaryPercentage) => {
      saveBeneficiaryAdults('Nineteen and older', percentage);
      setNineteen(percentage);
    },
    [beneficiary?.categories, setBeneficiaries, setNineteen]
  );
  const saveSeniors = useCallback(
    (percentage: BeneficiaryPercentage) => {
      saveBeneficiaryAdults('Seniors', percentage);
      setSeniors(percentage);
    },
    [beneficiary?.categories, setBeneficiaries, setSeniors]
  );
  const handleReset = useCallback(() => {
    saveAllAdults(null);
    saveNineteen(null);
    saveSeniors(null);
  }, [saveAllAdults, saveNineteen, saveSeniors]);

  return (
    <BeneficiaryMultiValues
      title="Adults"
      errorMessage={errorMessage}
      leftSide={
        <PercentageFieldWithLabel
          label="All Adults"
          initialValue={allAdults}
          onBlur={saveAllAdults}
          disabled={isDisabled('Adults', allAdults, nineteen, seniors)}
        />
      }
      rightSide={
        <>
          <PercentageFieldWithLabel
            label="Adults (19+)"
            initialValue={nineteen}
            // focusOnMount={true}
            onBlur={saveNineteen}
            disabled={isDisabled(
              'Nineteen and older',
              allAdults,
              nineteen,
              seniors
            )}
          />
          <PercentageFieldWithLabel
            label="Seniors (64+)"
            initialValue={seniors}
            onBlur={saveSeniors}
            className="field-margin-top"
            disabled={isDisabled('Seniors', allAdults, nineteen, seniors)}
          />
        </>
      }
      onReset={handleReset}
    />
  );
};

export default BeneficiaryAdultValues;
